// Fixed ProductForm.jsx - With image deletion support
import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import Button from '../common/Button';
import Input from '../common/Input';
import './ProductForm.css';

const ProductForm = ({ product, onSubmit, onCancel, loading }) => {
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [activeLanguage, setActiveLanguage] = useState('ka');
  const [existingImages, setExistingImages] = useState([]); // ✅ Track existing images
  const [deletingImageId, setDeletingImageId] = useState(null); // ✅ Track deletion state
  const [formData, setFormData] = useState({
    name_ka: '',
    description_ka: '',
    name_en: '',
    description_en: '',
    name_ru: '',
    description_ru: '',
    price: '',
    stock: 0,
    sku: '',
    category_id: '',
    brand_id: '',
    is_active: true,
    images: []
  });
  const [newImagePreviews, setNewImagePreviews] = useState([]); // ✅ Only for newly uploaded images

  useEffect(() => {
    fetchCategories();
    fetchBrands();
    if (product) {
      console.log('Product data received:', product);
      setFormData({
        name_ka: product.name_ka || product.name || '',
        description_ka: product.description_ka || product.description || '',
        name_en: product.name_en || '',
        description_en: product.description_en || '',
        name_ru: product.name_ru || '',
        description_ru: product.description_ru || '',
        price: product.price || '',
        stock: product.stock !== undefined ? product.stock : 0,
        sku: product.sku || '',
        category_id: product.category_id || '',
        brand_id: product.brand_id || '',
        is_active: product.is_active !== false,
        images: []
      });
      
      // ✅ Set existing images separately
      if (product.images && product.images.length > 0) {
        setExistingImages(product.images);
      }
    }
  }, [product]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/admin/categories');
      setCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get('/admin/brands');
      setBrands(response.data.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = value;
    if (type === 'number') {
      finalValue = value === '' ? 0 : Number(value);
    }
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : finalValue
    });
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData({ ...formData, images: files });
    const previews = files.map(file => URL.createObjectURL(file));
    setNewImagePreviews(previews);
  };

  // ✅ Delete existing image
  const handleDeleteExistingImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    setDeletingImageId(imageId);
    
    try {
      await api.delete(`/admin/products/${product.id}/images/${imageId}`);
      
      // Remove from existing images list
      setExistingImages(prev => prev.filter(img => img.id !== imageId));
      
      alert('Image deleted successfully!');
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image. Please try again.');
    } finally {
      setDeletingImageId(null);
    }
  };

  // ✅ Remove newly uploaded image (not yet saved)
  const handleRemoveNewImage = (index) => {
    const newFiles = Array.from(formData.images);
    newFiles.splice(index, 1);
    setFormData({ ...formData, images: newFiles });
    
    const newPreviews = [...newImagePreviews];
    newPreviews.splice(index, 1);
    setNewImagePreviews(newPreviews);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name_ka.trim()) {
      alert('Georgian product name is required!');
      setActiveLanguage('ka');
      return;
    }
    
    const data = new FormData();
    data.append('name_ka', formData.name_ka);
    data.append('description_ka', formData.description_ka || '');
    data.append('name_en', formData.name_en || '');
    data.append('description_en', formData.description_en || '');
    data.append('name_ru', formData.name_ru || '');
    data.append('description_ru', formData.description_ru || '');
    data.append('price', formData.price);
    data.append('stock', formData.stock);
    data.append('sku', formData.sku);
    data.append('category_id', formData.category_id);
    data.append('brand_id', formData.brand_id || '');
    data.append('is_active', formData.is_active);
    
    formData.images.forEach(image => {
      data.append('images', image);
    });
    
    onSubmit(data);
  };

  const mainCategories = categories.filter(cat => cat.parent_id === null);
  const subcategories = categories.filter(cat => cat.parent_id !== null);

  return (
    <form onSubmit={handleSubmit} className="form-container">
      {/* Language Tabs */}
      <div className="language-tabs">
        <button
          type="button"
          className={`language-tab ${activeLanguage === 'ka' ? 'active' : ''}`}
          onClick={() => setActiveLanguage('ka')}
        >
          🇬🇪 Georgian (ქართული) *
        </button>
        <button
          type="button"
          className={`language-tab ${activeLanguage === 'en' ? 'active' : ''}`}
          onClick={() => setActiveLanguage('en')}
        >
          🇬🇧 English
        </button>
        <button
          type="button"
          className={`language-tab ${activeLanguage === 'ru' ? 'active' : ''}`}
          onClick={() => setActiveLanguage('ru')}
        >
          🇷🇺 Russian (Русский)
        </button>
      </div>

      {/* Georgian Fields */}
      {activeLanguage === 'ka' && (
        <div className="language-content">
          <div className="language-note">
            <strong>ქართული</strong> - ძირითადი ენა (სავალდებულო)
          </div>
          <div className="form-grid">
            <div className="form-group">
              <Input
                label="Product Name (Georgian)"
                name="name_ka"
                value={formData.name_ka}
                onChange={handleChange}
                placeholder="მაგ: iPhone 15 Pro"
                required
              />
            </div>
            <div className="form-group full-width">
              <label className="form-label">Description (Georgian)</label>
              <textarea
                name="description_ka"
                value={formData.description_ka}
                onChange={handleChange}
                placeholder="პროდუქტის აღწერა..."
                className="form-textarea"
                rows="4"
              />
            </div>
          </div>
        </div>
      )}

      {/* English Fields */}
      {activeLanguage === 'en' && (
        <div className="language-content">
          <div className="language-note">
            <strong>English</strong> - Optional translation
          </div>
          <div className="form-grid">
            <div className="form-group">
              <Input
                label="Product Name (English)"
                name="name_en"
                value={formData.name_en}
                onChange={handleChange}
                placeholder="e.g., iPhone 15 Pro"
              />
            </div>
            <div className="form-group full-width">
              <label className="form-label">Description (English)</label>
              <textarea
                name="description_en"
                value={formData.description_en}
                onChange={handleChange}
                placeholder="Product description..."
                className="form-textarea"
                rows="4"
              />
            </div>
          </div>
        </div>
      )}

      {/* Russian Fields */}
      {activeLanguage === 'ru' && (
        <div className="language-content">
          <div className="language-note">
            <strong>Русский</strong> - Необязательный перевод
          </div>
          <div className="form-grid">
            <div className="form-group">
              <Input
                label="Product Name (Russian)"
                name="name_ru"
                value={formData.name_ru}
                onChange={handleChange}
                placeholder="например, iPhone 15 Pro"
              />
            </div>
            <div className="form-group full-width">
              <label className="form-label">Description (Russian)</label>
              <textarea
                name="description_ru"
                value={formData.description_ru}
                onChange={handleChange}
                placeholder="Описание продукта..."
                className="form-textarea"
                rows="4"
              />
            </div>
          </div>
        </div>
      )}

      {/* Common Fields */}
      <div className="common-fields">
        <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>
          Product Details (All Languages)
        </h3>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">
              Category <span style={{ color: 'var(--error)' }}>*</span>
            </label>
            <select
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              className="form-select"
              required
            >
              <option value="">Select category</option>
              {mainCategories.map(mainCat => (
                <optgroup key={mainCat.id} label={mainCat.name}>
                  <option value={mainCat.id}>{mainCat.name} (Main)</option>
                  {subcategories
                    .filter(sub => sub.parent_id === mainCat.id)
                    .map(subcat => (
                      <option key={subcat.id} value={subcat.id}>
                        ↳ {subcat.name}
                      </option>
                    ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Brand (Optional)</label>
            <select
              name="brand_id"
              value={formData.brand_id}
              onChange={handleChange}
              className="form-select"
            >
              <option value="">No brand</option>
              {brands.map(brand => (
                <option key={brand.id} value={brand.id}>
                  {brand.name || brand.name_ka}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <Input
              label="Price"
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              placeholder="99.99"
              step="0.01"
              required
            />
          </div>

          <div className="form-group">
            <Input
              label="Stock"
              type="number"
              name="stock"
              value={formData.stock}
              onChange={handleChange}
              placeholder="100"
              min="0"
              required
            />
          </div>

          <div className="form-group">
            <Input
              label="SKU"
              name="sku"
              value={formData.sku}
              onChange={handleChange}
              placeholder="PROD-001"
            />
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              style={{ width: 'auto' }}
            />
            <label htmlFor="is_active" style={{ marginBottom: 0 }}>Active Product</label>
          </div>

          <div className="form-group full-width">
            <label className="form-label">Product Images</label>
            
            {/* ✅ Existing Images (from database) */}
            {existingImages.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--gray)', marginBottom: '0.5rem' }}>
                  Current Images:
                </div>
                <div className="file-preview">
                  {existingImages.map((image) => (
                    <div key={image.id} style={{ position: 'relative' }}>
                      <img 
                        src={`https://kid-o.ge${image.image_url}`} 
                        alt="Product" 
                        className="preview-image"
                      />
                      {image.is_primary === 1 && (
                        <div style={{
                          position: 'absolute',
                          top: '5px',
                          left: '5px',
                          background: 'var(--primary)',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}>
                          PRIMARY
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDeleteExistingImage(image.id)}
                        disabled={deletingImageId === image.id}
                        style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          background: 'var(--error)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '28px',
                          height: '28px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.2rem',
                          fontWeight: 'bold',
                          opacity: deletingImageId === image.id ? 0.5 : 1
                        }}
                      >
                        {deletingImageId === image.id ? '⏳' : '×'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ✅ New Images Upload */}
            <div className="file-input-wrapper">
              <input
                type="file"
                id="product-images"
                className="file-input"
                accept="image/*"
                multiple
                onChange={handleImagesChange}
              />
              <label htmlFor="product-images" className="file-input-label">
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📷</div>
                  <div>Click to upload new images</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--gray)', marginTop: '0.5rem' }}>
                    PNG, JPG, WEBP (Max 5MB each)
                  </div>
                </div>
              </label>
            </div>

            {/* ✅ New Image Previews (not yet saved) */}
            {newImagePreviews.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--gray)', marginBottom: '0.5rem' }}>
                  New Images to Upload:
                </div>
                <div className="file-preview">
                  {newImagePreviews.map((preview, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img src={preview} alt={`New ${index + 1}`} className="preview-image" />
                      <button
                        type="button"
                        onClick={() => handleRemoveNewImage(index)}
                        style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          background: 'var(--error)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '28px',
                          height: '28px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.2rem',
                          fontWeight: 'bold'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="form-actions">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={loading}>
          {product ? 'Update Product' : 'Create Product'}
        </Button>
      </div>
    </form>
  );
};

export default ProductForm;